import React from 'react';
import { useApp } from '../context/AppContext';
import { MOCK_PRODUCTS, DOOR_TYPES } from '../data/mockData';
import { 
  Layers, 
  DoorOpen, 
  Package, 
  Wand2,
  CheckSquare,
  Truck
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export function FloorDetail({ floor, building }) {
  const { state, actions } = useApp();
  const { deliverySelections } = state;

  // Calculate statistics
  const doorsByType = floor.doors.reduce((acc, door) => {
    acc[door.type] = (acc[door.type] || 0) + 1;
    return acc;
  }, {});

  const totalProducts = floor.doors.reduce((sum, door) => sum + door.products.length, 0);
  const doorsWithProducts = floor.doors.filter(door => door.products.length > 0).length;

  // Calculate total cost
  const totalCost = floor.doors.reduce((sum, door) => {
    return sum + door.products.reduce((doorSum, productId) => {
      const product = MOCK_PRODUCTS.find(p => p.id === productId);
      return doorSum + (product?.price || 0);
    }, 0);
  }, 0);

  const handleAutoAssign = () => {
    actions.autoAssignHardware('floor', building.id, floor.id);
    toast.success('Hardware auto-assigned to all doors on this floor');
  };

  const isFloorSelected = deliverySelections.some(s => s.type === 'floor' && s.id === floor.id);

  const handleFloorSelect = () => {
    actions.toggleDeliverySelection('floor', floor.id, { buildingId: building.id, floorId: floor.id });
  };

  const isDoorSelected = (doorId) => {
    return deliverySelections.some(s => s.type === 'door' && s.id === doorId);
  };

  const handleDoorSelect = (door) => {
    actions.toggleDeliverySelection('door', door.id, { 
      buildingId: building.id, 
      floorId: floor.id, 
      doorId: door.id 
    });
  };

  const doorTypeColors = {
    'standard': 'bg-slate-100 text-slate-700 border-slate-200',
    'exit': 'bg-green-100 text-green-700 border-green-200',
    'fire-rated': 'bg-red-100 text-red-700 border-red-200'
  };

  return (
    <div className="space-y-6" data-testid="floor-detail">
      {/* Floor Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
            <Layers className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{floor.name}</h3>
            <p className="text-sm text-muted-foreground">{building.name}</p>
          </div>
        </div>
        <Button onClick={handleAutoAssign} data-testid="floor-auto-assign-btn">
          <Wand2 className="w-4 h-4 mr-2" />
          Auto Assign Hardware
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          label="Total Doors"
          value={floor.doors.length}
          icon={<DoorOpen className="w-4 h-4" />}
        />
        <StatCard 
          label="With Hardware"
          value={`${doorsWithProducts}/${floor.doors.length}`}
          icon={<Package className="w-4 h-4" />}
        />
        <StatCard 
          label="Total Products"
          value={totalProducts}
          icon={<CheckSquare className="w-4 h-4" />}
        />
        <StatCard 
          label="Total Cost"
          value={`$${totalCost.toFixed(0)}`}
          icon={<Truck className="w-4 h-4" />}
        />
      </div>

      {/* Door Type Breakdown */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Door Types
        </h4>
        <div className="flex gap-2">
          {DOOR_TYPES.map(type => (
            <Badge 
              key={type.id}
              variant="outline"
              className={cn(doorTypeColors[type.id])}
            >
              {type.name}: {doorsByType[type.id] || 0}
            </Badge>
          ))}
        </div>
      </div>

      {/* Door List */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Doors ({floor.doors.length})
        </h4>
        
        {floor.doors.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-6 text-center">
            <DoorOpen className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No doors on this floor</p>
          </div>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {floor.doors.map((door) => {
              const productCount = door.products.length;
              const doorCost = door.products.reduce((sum, productId) => {
                const product = MOCK_PRODUCTS.find(p => p.id === productId);
                return sum + (product?.price || 0);
              }, 0);

              return (
                <div 
                  key={door.id}
                  className={cn(
                    "flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors cursor-pointer",
                    isDoorSelected(door.id) && "bg-primary/5"
                  )}
                  onClick={() => actions.selectNode({ 
                    type: 'door', 
                    id: door.id, 
                    path: { buildingId: building.id, floorId: floor.id, doorId: door.id }
                  })}
                  data-testid={`floor-door-row-${door.id}`}
                >
                  <DoorOpen className={cn(
                    "w-4 h-4 shrink-0",
                    door.type === 'exit' && "text-green-600",
                    door.type === 'fire-rated' && "text-red-500",
                    door.type === 'standard' && "text-slate-500"
                  )} />
                  <span className="flex-1 text-sm font-medium">{door.name}</span>
                  <Badge variant="outline" className={cn("text-xs", doorTypeColors[door.type])}>
                    {door.type === 'fire-rated' ? 'Fire' : door.type}
                  </Badge>
                  <span className="text-xs text-muted-foreground w-20 text-right">
                    {productCount} products
                  </span>
                  <span className="text-sm font-medium w-16 text-right">
                    ${doorCost.toFixed(0)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="border border-border rounded-lg p-3">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}
